/**
 * BookHighlightsPanel - 読書ノートの上に出す、その本のハイライトの折りたたみ一覧。
 * 各ハイライトはリーダーでその位置を開くか、ノートのカーソル位置に引用できる
 */

import type { Editor } from '@tiptap/react'
import { ChevronRight, ExternalLink, Quote, StickyNote } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { type HighlightSource, listHighlightSources } from '@/api/services/highlight'
import {
  formatHighlightLocation,
  highlightColorHex,
  highlightReaderPath,
  highlightRefAttrs,
  putHighlightRefs,
} from '@/components/tiptap-node/highlight-ref-node'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

import { HighlightCardSuggestions } from './highlight-card-suggestions'

const MAX_HIGHLIGHTS = 500

type Props = {
  productId: number
  /** 見出しに出す件数（一覧を読み込む前の表示用） */
  count: number
  editor: Editor | null
}

const episodeLabel = (h: HighlightSource): string => h.episode_title?.trim() || `第${h.episode_index}話`

export const BookHighlightsPanel = ({ productId, count, editor }: Props) => {
  const [open, setOpen] = useState(false)
  const [highlights, setHighlights] = useState<HighlightSource[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [loadKey, setLoadKey] = useState(0)
  // カードの候補を開いているハイライト
  const [suggestingId, setSuggestingId] = useState<number | null>(null)

  // 初めて開いたときに読み込む
  useEffect(() => {
    if (!open || highlights !== null) return
    let cancelled = false
    listHighlightSources({ product_id: productId, limit: MAX_HIGHLIGHTS })
      .then((list) => {
        if (!cancelled) setHighlights(list)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [open, highlights, productId, loadKey])

  const quote = (h: HighlightSource) => {
    if (!editor) return
    putHighlightRefs([h])
    editor
      .chain()
      .focus()
      .insertContent({ type: 'highlightRef', attrs: highlightRefAttrs(h) })
      .run()
  }

  const hasMultipleEpisodes = new Set(highlights?.map((h) => h.episode_id)).size > 1

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mx-6 mb-3 rounded-md border sm:mx-0">
      <CollapsibleTrigger className="flex w-full cursor-pointer items-center gap-1.5 px-3 py-2 text-sm font-medium">
        <ChevronRight className={cn('size-4 shrink-0 transition-transform', open && 'rotate-90')} />
        この本のハイライト
        <span className="text-xs font-normal text-muted-foreground">{highlights?.length ?? count} 件</span>
      </CollapsibleTrigger>

      <CollapsibleContent className="border-t px-3 py-2">
        {failed ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            読み込めませんでした
            <button
              type="button"
              className="ml-2 underline"
              onClick={() => {
                setFailed(false)
                setLoadKey((k) => k + 1)
              }}
            >
              再読み込み
            </button>
          </p>
        ) : highlights === null ? (
          <div className="flex justify-center py-4">
            <Spinner size={20} />
          </div>
        ) : highlights.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">ハイライトはまだありません</p>
        ) : (
          <ul className="max-h-[50dvh] space-y-2 overflow-y-auto">
            {highlights.map((h, i) => {
              const location = formatHighlightLocation(h)
              const showEpisode = hasMultipleEpisodes && highlights[i - 1]?.episode_id !== h.episode_id
              return (
                <li key={h.id}>
                  {showEpisode && (
                    <p className="mt-2 mb-1 text-xs font-medium text-muted-foreground">{episodeLabel(h)}</p>
                  )}
                  <div className="flex gap-2">
                    <span
                      className="w-1 shrink-0 self-stretch rounded-full"
                      style={{ backgroundColor: highlightColorHex(h.color) }}
                    />
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{h.text || '（本文なし）'}</p>
                      {h.note && <p className="text-xs text-muted-foreground">✎ {h.note}</p>}
                      <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        <Link
                          to={highlightReaderPath(h)}
                          className="flex items-center gap-0.5 rounded px-0.5 hover:bg-accent hover:text-foreground"
                        >
                          <ExternalLink className="size-3" />
                          {location ?? '開く'}
                        </Link>
                        <button
                          type="button"
                          onClick={() => quote(h)}
                          disabled={!editor}
                          title="ノートのカーソル位置に引用"
                          className="flex items-center gap-0.5 rounded px-0.5! hover:bg-accent hover:text-foreground disabled:opacity-40"
                        >
                          <Quote className="size-3" />
                          引用
                        </button>
                        <button
                          type="button"
                          onClick={() => setSuggestingId((id) => (id === h.id ? null : h.id))}
                          aria-expanded={suggestingId === h.id}
                          title="このハイライトを入れるカードを選ぶ"
                          className="flex items-center gap-0.5 rounded px-0.5! hover:bg-accent hover:text-foreground"
                        >
                          <StickyNote className="size-3" />
                          カードへ
                        </button>
                      </div>
                      {suggestingId === h.id && <HighlightCardSuggestions highlightId={h.id} />}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}
